import {
  Badge,
  Box,
  Collapse,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Title,
  UnstyledButton
} from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import {
  mapCashupCalculationDetail,
  type CashupCalculationDetailGroup,
  type CashupReportPreview
} from '@washday/utilities';
import moment from 'moment';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoneyString } from '../../utils/strings';

interface CashupCalculationDetailProps {
  preview?: CashupReportPreview;
  isCashOnly: boolean;
}

type DetailGroup = CashupCalculationDetailGroup & {
  title?: string;
  description?: string;
};

export default function CashupCalculationDetail({
  preview,
  isCashOnly
}: CashupCalculationDetailProps) {
  const { t } = useTranslation(['CashupsPage', 'FieldName']);
  const [opened, setOpened] = useState(false);
  const contentId = useId();

  const groups: DetailGroup[] = mapCashupCalculationDetail(preview, isCashOnly).map((group) => {
    if (group.key === 'cash') {
      return {
        ...group,
        title: t('CashupsPage:calculationDetail.cashIncludedTitle'),
        description: t('CashupsPage:calculationDetail.cashIncludedDescription')
      };
    }

    if (group.key === 'nonCash') {
      return {
        ...group,
        title: t('CashupsPage:calculationDetail.nonCashInformationalTitle'),
        description: t('CashupsPage:calculationDetail.nonCashInformationalDescription')
      };
    }

    return group;
  });

  const formatPaymentMethod = (paymentMethod: string | null) => {
    const normalizedMethod = paymentMethod?.toLowerCase();
    if (
      normalizedMethod === 'cash' ||
      normalizedMethod === 'card' ||
      normalizedMethod === 'transfer' ||
      normalizedMethod === 'mercadopago'
    ) {
      return t(`CashupsPage:cashUpReport.paymentMethods.${normalizedMethod}`);
    }

    return paymentMethod || '-';
  };

  const formatDate = (date: Date | string) => {
    const parsedDate = moment(date);
    return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY HH:mm') : '-';
  };

  return (
    <Paper withBorder radius="sm" p="sm">
      <UnstyledButton
        type="button"
        aria-expanded={opened}
        aria-controls={contentId}
        onClick={() => setOpened((current) => !current)}
        style={{ width: '100%' }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Text fw={600}>{t('CashupsPage:calculationDetail.title')}</Text>
          {opened ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
        </Group>
      </UnstyledButton>

      <Collapse in={opened}>
        {opened && (
          <Stack id={contentId} gap="lg" mt="md">
            {groups.map((group) => (
              <Stack key={group.key} gap="md">
                {group.title && (
                  <Box>
                    <Title order={5}>{group.title}</Title>
                    {group.description && (
                      <Text size="sm" c="dimmed">
                        {group.description}
                      </Text>
                    )}
                  </Box>
                )}

                <Stack gap="xs">
                  <Title order={6}>{t('CashupsPage:calculationDetail.paymentsTitle')}</Title>
                  {group.payments.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      {t('CashupsPage:calculationDetail.emptyPayments')}
                    </Text>
                  ) : (
                    <Box style={{ overflowX: 'auto' }}>
                      <Table striped highlightOnHover withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>{t('CashupsPage:cashUpReport.order')}</Table.Th>
                            <Table.Th>{t('CashupsPage:cashUpReport.method')}</Table.Th>
                            <Table.Th>{t('CashupsPage:cashUpReport.date')}</Table.Th>
                            <Table.Th ta="right">{t('FieldName:amount')}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {group.payments.map(({ key, folio, paymentMethod, date, amount }) => (
                            <Table.Tr key={key}>
                              <Table.Td>{folio}</Table.Td>
                              <Table.Td>{formatPaymentMethod(paymentMethod)}</Table.Td>
                              <Table.Td style={{ whiteSpace: 'nowrap' }}>
                                {formatDate(date)}
                              </Table.Td>
                              <Table.Td ta="right">{formatMoneyString(amount)}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Box>
                  )}
                </Stack>

                <Stack gap="xs">
                  <Title order={6}>{t('CashupsPage:calculationDetail.movementsTitle')}</Title>
                  {group.movements.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      {t('CashupsPage:calculationDetail.emptyMovements')}
                    </Text>
                  ) : (
                    <Box style={{ overflowX: 'auto' }}>
                      <Table striped highlightOnHover withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>{t('CashupsPage:cashUpReport.type')}</Table.Th>
                            <Table.Th>{t('CashupsPage:cashUpReport.method')}</Table.Th>
                            <Table.Th>{t('CashupsPage:cashUpReport.date')}</Table.Th>
                            <Table.Th>{t('CashupsPage:cashUpReport.notes')}</Table.Th>
                            <Table.Th ta="right">{t('FieldName:amount')}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {group.movements.map((movement) => (
                            <Table.Tr key={movement.key}>
                              <Table.Td>
                                <Group gap="xs" wrap="nowrap">
                                  <Text size="sm">
                                    {t(`CashupsPage:cashUpReport.${movement.type}`)}
                                  </Text>
                                  {movement.isExpense && (
                                    <Badge size="sm" variant="light" color="orange">
                                      {t('CashupsPage:calculationDetail.expense')}
                                    </Badge>
                                  )}
                                </Group>
                              </Table.Td>
                              <Table.Td>{formatPaymentMethod(movement.paymentMethod)}</Table.Td>
                              <Table.Td style={{ whiteSpace: 'nowrap' }}>
                                {formatDate(movement.date)}
                              </Table.Td>
                              <Table.Td>{movement.notes || '-'}</Table.Td>
                              <Table.Td ta="right">{formatMoneyString(movement.amount)}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Box>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Collapse>
    </Paper>
  );
}
